import React from "react";

import { HighlightedText } from "@/components/suggested-redlines/components/highlighted-text";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  AccordionToggleData,
  Button,
  Caption1,
  Spinner,
} from "@fluentui/react-components";
import { QuestionCircle16Regular, ChevronUp16Regular } from "@fluentui/react-icons";
import { Redline } from "@/types/risk-analysis";
import { useSuggestedRedlinesStyles } from "@/components/suggested-redlines/styles";
import { RedlineError } from "@/hooks/use-redlines";
import { diffWithMetadata, ChangeType } from "@/taskpane/lib/common/diff";

interface RedlineCardProps {
  piece: Redline["pieces"][number];
  openItems: Record<string, boolean>;
  handleToggle: (data: AccordionToggleData) => void;
  handleShowRedlines: () => void;
  handleApplyRedlines: () => void;
  isLoading?: boolean;
  error?: RedlineError;
}

export const RedlineCard = ({
  piece,
  openItems,
  handleToggle,
  handleShowRedlines,
  handleApplyRedlines,
  isLoading,
}: RedlineCardProps) => {
  const styles = useSuggestedRedlinesStyles();

  // Use the new diff function with metadata
  const diffResult =
    piece.changeType === "add"
      ? diffWithMetadata("", piece.insertedText)
      : diffWithMetadata(piece.originalSentence, piece.replaceWith);

  // For compatibility with HighlightedText, convert back to old format
  const textChanges = diffResult.changes.map((change) => ({
    added: change.type === ChangeType.Add,
    removed: change.type === ChangeType.Remove,
    value: change.text,
  }));

  return (
    <>
      <div className={styles.redlineSentence}>
        {piece.changeType !== "add" && (
          <>
            <HighlightedText diffWords={textChanges} isOriginal={false} />
          </>
        )}
        {piece.changeType === "add" && <HighlightedText diffWords={textChanges} isOriginal={false} />}
      </div>
      <Accordion
        openItems={openItems[piece.id] ? [piece.id] : []}
        collapsible
        onToggle={(_, data) => handleToggle(data)}
      >
        <AccordionItem value={piece.id}>
          <AccordionPanel>
            <span>{piece.summary}</span>
          </AccordionPanel>
          <AccordionHeader
            style={{ position: "absolute" }}
            expandIcon={openItems[piece.id] ? <ChevronUp16Regular /> : <QuestionCircle16Regular />}
          >
            Explanation
          </AccordionHeader>
        </AccordionItem>
      </Accordion>
      <div className={styles.redlineButtonGroup}>
        <Button appearance="outline" size="small" onClick={handleShowRedlines} aria-label="Show">
          Highlight
        </Button>
        <Button disabled={isLoading} appearance="primary" size="small" onClick={handleApplyRedlines} aria-label="Apply">
          {isLoading ? <Spinner size="extra-tiny" label="Applying..." /> : "Apply"}
        </Button>
      </div>
      {piece.changeType === "add" && (
        <Caption1 color="primary">Text will be applied before the highlighted language</Caption1>
      )}
    </>
  );
};
